import type { BoardData } from '../model/schema.js';
import { fieldColumns } from './columns.js';
import { mixOutputs, outputsNeeded } from './mix-outputs.js';
import { itemName } from '../trace/ports.js';
import { byNumber } from './helpers.js';

export interface MonitorListRow {
  rowKey: string;
  mixId: string;
  number: number;
  name: string;
  kind: string;
  stereo: boolean;
  /** Names of the wedges / sidefills / IEMs fed by the mix. */
  outputs: string;
  outputCount: number;
  /** Stage-box outputs the mix plays on (U77): "SB1/5, SB1/6". */
  outputPatch: string;
  /** The outputs come from drawn cables (else informal `Mix.outputs`). */
  outputPatchFormal: boolean;
  /** Outputs are known but their count does not fit mono (1) / stereo (2). */
  outputPatchMismatch: boolean;
  /** Channels sent to the mix, ordered by channel number, with a set level (`1 Kick, 2 Snare (-6 dB)`). */
  sends: string;
  sendCount: number;
  notes: string;
}

/**
 * " (-6 dB)" / " (+3 dB)" / " (0 dB)" for a send with a level (OIL-175); "" without. German
 * writes a decimal comma: " (-3,5 dB)" (OIL-223).
 */
export function sendLevel(level: number | undefined, locale: 'en' | 'de' = 'en'): string {
  if (level === undefined) return '';
  const text = locale === 'de' ? String(level).replace('.', ',') : String(level);
  return ` (${level > 0 ? '+' : ''}${text} dB)`;
}

/** Monitor list: one row per mix, ordered by mix number. Missing references are skipped. */
export function monitorList(data: BoardData, locale: 'en' | 'de' = 'en'): MonitorListRow[] {
  return Object.values(data.mixes)
    .map((mix): MonitorListRow => {
      const outputs = mix.outputItemIds.flatMap((id) => {
        const item = data.items[id];
        return item === undefined ? [] : [item];
      });
      const patched = mixOutputs(data, mix);
      const sends = mix.sends
        .flatMap((s) => {
          const ch = data.channels[s.channelId];
          return ch === undefined ? [] : [{ ch, level: s.level }];
        })
        .sort((a, b) => a.ch.number - b.ch.number);
      return {
        rowKey: mix.id,
        mixId: mix.id,
        number: mix.number,
        name: mix.name,
        kind: mix.kind,
        stereo: mix.stereo,
        outputs: outputs.map(itemName).join(', '),
        outputCount: outputs.length,
        outputPatch: patched.map((o) => o.label).join(', '),
        outputPatchFormal: patched.some((o) => o.formal),
        outputPatchMismatch: patched.length > 0 && patched.length !== outputsNeeded(mix),
        sends: sends
          .map(({ ch, level }) => `${ch.number} ${ch.name}`.trim() + sendLevel(level, locale))
          .join(', '),
        sendCount: sends.length,
        notes: mix.notes ?? '',
      };
    })
    .sort(byNumber);
}

export const MONITOR_LIST_COLUMNS = fieldColumns<MonitorListRow>('monitor', [
  'number',
  'name',
  'kind',
  'stereo',
  'outputs',
  'outputPatch',
  'sends',
  'notes',
]).map((c) => (c.key === 'kind' ? { ...c, enumKey: 'tables.mixKind' } : c));
